import React from 'react';
import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: 'Helvetica',
  },
  headerContainer: {
    marginBottom: 30,
    borderBottom: 2,
    borderBottomColor: '#333',
    paddingBottom: 10,
  },
  businessHeader: {
    fontSize: 24,
    textAlign: 'center',
    marginBottom: 10,
    color: '#1a365d',
  },
  businessInfo: {
    fontSize: 10,
    textAlign: 'center',
    color: '#4a5568',
    marginBottom: 5,
  },
  documentTitle: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 20,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
    color: '#2563eb',
  },
  dateSection: {
    fontSize: 11,
    marginBottom: 20,
    color: '#4b5563',
    textAlign: 'right',
  },
  vehicleDetails: {
    marginBottom: 20,
    padding: 12,
    backgroundColor: '#f7fafc',
    borderRadius: 4,
  },
  vehicleHeader: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 8,
    color: '#1f2937',
    borderBottom: 1,
    borderBottomColor: '#e5e7eb',
    paddingBottom: 4,
  },
  vehicleInfo: {
    fontSize: 10,
    marginBottom: 4,
    color: '#4b5563',
  },
  orderContainer: {
    marginBottom: 15,
    padding: 12,
    borderRadius: 4,
    border: 1,
    borderColor: '#e5e7eb',
  },
  orderHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
    paddingBottom: 4,
    borderBottom: 1,
    borderBottomColor: '#e5e7eb',
  },
  customerName: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    color: '#1f2937',
  },
  orderNumber: {
    fontSize: 10,
    color: '#6b7280',
  },
  contactInfo: {
    marginBottom: 8,
    padding: 8,
    backgroundColor: '#f9fafb',
  },
  contactText: {
    fontSize: 10,
    marginBottom: 2,
    color: '#4b5563',
  },
  itemsHeader: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    marginTop: 8,
    marginBottom: 4,
    color: '#1f2937',
  },
  itemsList: {
    paddingLeft: 12,
  },
  item: {
    fontSize: 10,
    marginBottom: 3,
    color: '#4b5563',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    color: '#6b7280',
    fontSize: 10,
    borderTop: 1,
    borderTopColor: '#e5e7eb',
    paddingTop: 10,
  },
  pageNumber: {
    position: 'absolute',
    bottom: 30,
    right: 30,
    fontSize: 10,
    color: '#6b7280',
  },
  table: {
    display: 'table',
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
  },
  tableHeader: {
    backgroundColor: '#f3f4f6',
    fontFamily: 'Helvetica-Bold',
  },
  tableCell: {
    flex: 1,
    padding: 8,
    fontSize: 10,
    borderRightWidth: 1,
    borderRightColor: '#000',
    color: '#000',
  },
  customerHeader: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
  },
  dateHeader: {
    marginBottom: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  notes: {
    marginTop: 8,
    fontSize: 10,
    fontStyle: 'italic',
    color: '#6b7280',
  },
});

const DeliveryList = ({ orders, date, vehicle }) => {
  // Get unique vegetables across all orders
  const vegetables = [...new Set(orders.flatMap(order => 
    order.items.map(item => item.vegetable)
  ))];

  const formatQuantity = (item) => {
    if (!item) return '';
    
    // If the item was originally entered as boxes, show box quantity
    if (item.originalUnit === 'box') {
      return `${item.originalQuantity} box`;
    }
    // Otherwise show kg
    return `${item.estimatedQuantity} kg`;
  };

  // Calculate totals for each vegetable
  const calculateTotals = () => {
    return vegetables.reduce((acc, veg) => {
      const items = orders.flatMap(order => 
        order.items.filter(item => item.vegetable === veg)
      );

      const kgTotal = items
        .filter(item => item.originalUnit !== 'box')
        .reduce((sum, item) => sum + (Number(item.estimatedQuantity) || 0), 0);

      const boxTotal = items
        .filter(item => item.originalUnit === 'box')
        .reduce((sum, item) => sum + (Number(item.originalQuantity) || 0), 0);

      acc[veg] = {
        kg: kgTotal,
        boxes: boxTotal
      };

      return acc;
    }, {});
  };

  const totals = calculateTotals();

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.dateHeader}>
          <Text>Date: {new Date(date).toLocaleDateString()}</Text>
          <Text>Vehicle: {vehicle?.registrationNumber || 'N/A'}</Text>
        </View>

        <View style={styles.table}>
          {/* Header Row */}
          <View style={[styles.tableRow, styles.tableHeader]}>
            <Text style={[styles.tableCell, { fontFamily: 'Helvetica-Bold' }]}>Customer</Text>
            {vegetables.map((veg, index) => (
              <Text key={index} style={[styles.tableCell, { fontFamily: 'Helvetica-Bold' }]}>{veg}</Text>
            ))}
            <Text style={[styles.tableCell, { fontFamily: 'Helvetica-Bold' }]}>Amount Received</Text>
            <Text style={[styles.tableCell, { fontFamily: 'Helvetica-Bold' }]}>Boxes Given</Text>
            <Text style={[styles.tableCell, { fontFamily: 'Helvetica-Bold' }]}>Boxes Returned</Text>
            <Text style={[styles.tableCell, { fontFamily: 'Helvetica-Bold' }]}>Balance</Text>
          </View>

          {/* Customer Rows */}
          {orders.map((order, index) => (
            <View key={index} style={styles.tableRow}>
              <Text style={styles.tableCell}>{order.customerName}</Text>
              {vegetables.map((veg, vegIndex) => {
                const item = order.items.find(i => i.vegetable === veg);
                return (
                  <Text key={vegIndex} style={styles.tableCell}>
                    {formatQuantity(item)}
                  </Text>
                );
              })}
              <Text style={styles.tableCell}>
                {order.amountReceived ? `₹${order.amountReceived}` : ''}
              </Text>
              <Text style={styles.tableCell}>{''}</Text>
              <Text style={styles.tableCell}>{''}</Text>
              <Text style={styles.tableCell}>{''}</Text>
            </View>
          ))}

          {/* Totals Row */}
          <View style={[styles.tableRow, { backgroundColor: '#f3f4f6' }]}>
            <Text style={[styles.tableCell, { fontFamily: 'Helvetica-Bold' }]}>Total</Text>
            {vegetables.map((veg, index) => (
              <Text key={index} style={[styles.tableCell, { fontFamily: 'Helvetica-Bold' }]}>
                {totals[veg].kg > 0 && `${totals[veg].kg}kg`}
                {totals[veg].kg > 0 && totals[veg].boxes > 0 && ' + '}
                {totals[veg].boxes > 0 && `${totals[veg].boxes}box`}
              </Text>
            ))}
            <Text style={styles.tableCell}>{''}</Text>
            <Text style={styles.tableCell}>{''}</Text>
            <Text style={styles.tableCell}>{''}</Text>
            <Text style={styles.tableCell}>{''}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default DeliveryList;